/**
 * R5Card
 * -----------------------------------------------------------
 * Treasure-map card with brass-corner brackets, image header,
 * category eyebrow, title, blurb, meta row. Locked R5-M1 design.
 *
 * Renders an <a> if `href`, a <button> if `onClick`, otherwise
 * an <article>. Accepts the following content slots:
 *   - badge:        small "Featured Tonight" pill at top of body
 *   - ribbon:       <R5Ribbon> child overlaid on top-left
 *   - imageBg:      CSS background-image string for header image
 *                   (use for gradients / non-image backgrounds)
 *   - imageSrc:     raw image URL — rendered via <SmartImage> for
 *                   progressive loading + responsive variants. Prefer
 *                   this over `imageBg` for real photos.
 *   - imageAlt:     alt text when imageSrc is used
 *   - image:        replacement node (e.g. <img>) for header
 *   - priceOverlay: small badge anchored bottom-right of image
 *   - category:     uppercase eyebrow line
 *   - title, blurb: main copy
 *   - meta:         meta row at bottom (any node)
 *   - footer:       replaces meta row entirely
 *   - children:     replaces the entire body when supplied
 *   - featured:     applies r5-card--featured (taller image / type)
 */
import React from 'react';
import { SmartImage } from '../../OptimizedImage';

export function R5Card({
  href,
  onClick,
  badge,
  ribbon,
  imageBg,
  imageSrc,
  imageAlt = '',
  image,
  priceOverlay,
  category,
  title,
  blurb,
  meta,
  footer,
  featured = false,
  className = '',
  children,
  ...rest
}) {
  const cls = [
    'r5-card',
    featured && 'r5-card--featured',
    className,
  ].filter(Boolean).join(' ');

  const imageStyle = imageBg ? { backgroundImage: imageBg } : undefined;
  const hasImageSlot = !!(image || imageSrc || imageBg);

  const inner = (
    <>
      {ribbon}
      {hasImageSlot && (
        <div className="r5-card__image" style={imageStyle}>
          {imageSrc ? (
            <SmartImage
              src={imageSrc}
              alt={imageAlt}
              aspectRatio={featured ? 16 / 10 : 4 / 3}
              objectFit="cover"
              className="r5-card__image-inner"
            />
          ) : null}
          {image}
          {priceOverlay && (
            <span className="r5-card__price-overlay">{priceOverlay}</span>
          )}
        </div>
      )}
      {children !== undefined ? (
        children
      ) : (
        <div className="r5-card__body">
          {badge && <span className="r5-card__featured-badge">{badge}</span>}
          {category && <div className="r5-card__category">{category}</div>}
          {title && <h3 className="r5-card__title">{title}</h3>}
          {blurb && <p className="r5-card__blurb">{blurb}</p>}
          {footer !== undefined
            ? footer
            : meta && <div className="r5-card__meta">{meta}</div>}
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <a className={cls} href={href} {...rest}>
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick} {...rest}>
        {inner}
      </button>
    );
  }
  return (
    <article className={cls} {...rest}>
      {inner}
    </article>
  );
}

export default R5Card;
